"use client";

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import { mogelijkIngelogd } from "@/lib/auth-opslag";
import { useLaadNaInteractie } from "@/lib/use-load-after-intent";

export type UserRole = "admin" | "shopbuilder" | "editor" | "customer";

/**
 * Hoe nadrukkelijk een component de inlogstatus nodig heeft (MOW-PERF-OKT26, bundelslanking):
 * de Auth-SDK (±130 KB geparseerd) zit niet meer in de eerste bundel van elke pagina maar wordt
 * pas geladen als iemand de status echt nodig heeft.
 *  - "altijd":     start de SDK meteen. Voor alles wat inlogt, uitlogt, een rol afdwingt of de
 *                  `auth`-instantie zelf gebruikt (account, afrekenen, beheer). Standaard.
 *  - "bij-sporen": start de SDK alleen als de browser sporen van een eerdere login heeft
 *                  (lib/auth-opslag.ts). Anders is er zeker niemand ingelogd en is `loading` meteen
 *                  false met `user` null, zonder de SDK te laden. Voor publieke pagina's die iets
 *                  met een ingelogde bezoeker doen (bijv. beoordelingen).
 *  - "meelezen":   vraagt niets aan en leest alleen mee. De provider zelf kijkt na het laden (of bij
 *                  de eerste aanraking) of er sporen zijn. Voor onderdelen in elke pagina (Klaviyo).
 */
export type AuthModus = "altijd" | "bij-sporen" | "meelezen";

interface AuthContextType {
  user: User | null;
  role: UserRole | null;
  loading: boolean;
  /** Intern: gebruik useAuth(modus). */
  vraagAan: (modus: AuthModus) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  role: null,
  loading: true,
  vraagAan: () => {},
});

/** Stuurt een login-event naar de server zodat het in het logboek verschijnt. */
async function logLogin(user: User): Promise<void> {
  try {
    const token = await user.getIdToken();
    await fetch("/api/auth/log-login", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // Logging mag de gebruiker nooit blokkeren
  }
}

export default function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  // Houdt bij welke UID al is gelogd in deze browser-sessie,
  // zodat token-refreshes (elk uur) niet opnieuw worden gelogd.
  const loggedUids = useRef<Set<string>>(new Set());

  // Of de SDK is gestart en bijbehorende opruimfunctie. Ook de sporencontrole van "bij-sporen"
  // wordt maar één keer gedaan.
  const gestart = useRef(false);
  const sporenGecontroleerd = useRef(false);
  const opruimen = useRef<(() => void) | null>(null);

  const startSdk = useCallback(() => {
    if (gestart.current) return;
    gestart.current = true;
    let afgebroken = false;
    const abonnementen: (() => void)[] = [];
    opruimen.current = () => {
      afgebroken = true;
      abonnementen.forEach((stop) => stop());
    };

    (async () => {
      const [{ onIdTokenChanged, onAuthStateChanged, getIdTokenResult }, { auth }] = await Promise.all([
        import("firebase/auth"),
        import("@/lib/firebase-auth"),
      ]);
      if (afgebroken) return;

      // onAuthStateChanged detecteert echte sign-in / sign-out events
      abonnementen.push(
        onAuthStateChanged(auth, async (firebaseUser) => {
          if (firebaseUser && !loggedUids.current.has(firebaseUser.uid)) {
            // Kleine vertraging zodat de token-claims al beschikbaar zijn
            setTimeout(() => logLogin(firebaseUser), 500);
            loggedUids.current.add(firebaseUser.uid);
          }
          if (!firebaseUser) {
            // Reset bij uitloggen zodat volgend inloggen opnieuw gelogd wordt
            loggedUids.current.clear();
          }
        })
      );

      // onIdTokenChanged handelt token-refreshes en rol-updates af.
      // LET OP: hier géén setLoading(true) — loading staat initieel al op true en
      // wordt éénmalig false. Firebase ververst het token ~elk uur; loading weer
      // aanzetten liet layouts (admin) hun "Laden…"-fallback tonen, waardoor de
      // hele pagina unmountte en onopgeslagen formulierwijzigingen verloren gingen.
      // "; Secure" op https, leeg op http (localhost) — anders valt de cookie weg.
      const cookieSecure =
        typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
      abonnementen.push(
        onIdTokenChanged(auth, async (firebaseUser) => {
          if (firebaseUser) {
            setUser(firebaseUser);
            try {
              // GEEN geforceerde refresh (tweede argument true) hier! Een force mint
              // een nieuw token → dat triggert onIdTokenChanged opnieuw → weer een
              // force… een lus die de pagina elke ± minuut liet verversen. De claims
              // uit het huidige token volstaan; flows die een rol wijzigen (zoals
              // "Word beheerder") forceren zelf al een refresh via getIdToken(true).
              const tokenResult = await getIdTokenResult(firebaseUser);
              const userRole = (tokenResult.claims.role as UserRole) || "customer";
              setRole(userRole);

              // Rol-cookie voor de proxy/middleware. Alléén een UX-gate: de echte
              // autorisatie gebeurt server-side (requireRole, checkRevoked). Secure
              // op https zodat de cookie niet over http meelekt; op localhost (http)
              // zou Secure de cookie laten wegvallen en de proxy-gate breken.
              document.cookie = `role=${userRole}; path=/; max-age=3600; SameSite=Strict${cookieSecure}`;
            } catch (error) {
              console.error("Error fetching ID token claims:", error);
              setRole("customer");
              document.cookie =
                `role=customer; path=/; max-age=3600; SameSite=Strict${cookieSecure}`;
            }
          } else {
            setUser(null);
            setRole(null);
            // Clear cookie
            document.cookie = `role=; path=/; max-age=0; SameSite=Strict${cookieSecure}`;
          }
          setLoading(false);
        })
      );
    })().catch((error) => {
      // De SDK-chunk kon niet geladen worden (bijv. offline). Zonder dit bleef `loading` voor
      // altijd true en hing een beheer- of accountpagina op "Laden…". Een volgende aanvraag mag
      // opnieuw proberen.
      console.error("Auth-SDK laden mislukt:", error);
      if (!afgebroken) {
        gestart.current = false;
        setLoading(false);
      }
    });
  }, []);

  const vraagAan = useCallback(
    (modus: AuthModus) => {
      if (modus === "altijd") {
        startSdk();
        return;
      }
      if (modus === "meelezen" || gestart.current || sporenGecontroleerd.current) return;
      sporenGecontroleerd.current = true;
      mogelijkIngelogd().then((mogelijk) => {
        if (mogelijk) startSdk();
        else setLoading(false); // niemand ingelogd: user en role blijven null
      });
    },
    [startSdk]
  );

  // Zelf kijken zodra de bezoeker iets doet of een tijd na het laden: dan is een teruggekeerde, nog
  // ingelogde klant ook op pagina's waar niets de status vraagt bekend (Klaviyo-identificatie, de
  // rol-cookie), zonder dat de SDK midden in het laden binnenkomt. Voor bezoekers zonder sporen
  // gebeurt er niets behalve een controle van een paar milliseconden.
  useLaadNaInteractie(() => vraagAan("bij-sporen"));

  // Opruimen bij unmount (ook de gesimuleerde unmount van React Strict Mode in dev: daarna starten de
  // aanvragen van de componenten opnieuw, dus de vlaggen moeten terug).
  useEffect(() => {
    return () => {
      opruimen.current?.();
      opruimen.current = null;
      gestart.current = false;
      sporenGecontroleerd.current = false;
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, role, loading, vraagAan }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(modus: AuthModus = "altijd") {
  const { user, role, loading, vraagAan } = useContext(AuthContext);
  useEffect(() => {
    vraagAan(modus);
  }, [vraagAan, modus]);
  return { user, role, loading };
}
